import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Button,
  ButtonText,
  ButtonIcon,
  Spinner,
  Center,
  ScrollView,
  Pressable,
  Icon,
  Badge,
  BadgeText,
} from '@gluestack-ui/themed';
import {
  Clock, TrendingUp, TrendingDown, Minus, RotateCcw,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from '../../../../common/utils/formatRelativeTime';

interface TaskSubmissionVersionSummary {
  versionSeq: number;
  createdAt: string;
  sizeChars: number;
  pinned: boolean;
  contentHash: string;
}

interface VersionHistoryPanelProps {
  versions: TaskSubmissionVersionSummary[];
  currentCharCount?: number;
  isLoading: boolean;
  isError: boolean;
  onLoadVersion: (versionSeq: number) => void;
}

/**
 * Formats character difference with appropriate sign and color
 */
function formatCharDiff(versionChars: number, currentChars: number): {
  text: string;
  color: string;
  icon: typeof TrendingUp | typeof TrendingDown | typeof Minus;
} {
  const diff = versionChars - currentChars;

  if (diff === 0) {
    return { text: 'Same length', color: '$textSubtle', icon: Minus };
  }

  if (diff > 0) {
    return {
      text: `+${diff.toLocaleString()} chars`,
      color: '$success600',
      icon: TrendingUp,
    };
  }

  return {
    text: `${diff.toLocaleString()} chars`,
    color: '$error600',
    icon: TrendingDown,
  };
}

export const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  versions,
  currentCharCount = 0,
  isLoading,
  isError,
  onLoadVersion,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Center flex={1} testID="version-history-loading">
        <Spinner size="large" />
        <Text size="sm" mt="$4" color="$textSubtle">
          {t('student.task.versionHistory.loading', 'Loading version history...')}
        </Text>
      </Center>
    );
  }

  if (isError) {
    return (
      <Center flex={1} px="$4" testID="version-history-error">
        <Text size="sm" color="$error500">
          {t('student.task.versionHistory.error', 'Failed to load version history. Please try again.')}
        </Text>
      </Center>
    );
  }

  if (!Array.isArray(versions) || versions.length === 0) {
    return (
      <Center flex={1} px="$4" testID="version-history-empty">
        <Icon as={Clock} size="xl" color="$textHint" mb="$2" />
        <Text size="sm" color="$textSubtle" textAlign="center">
          {t('student.task.versionHistory.empty', 'No previous versions available')}
        </Text>
        <Text size="xs" color="$textHint" textAlign="center" mt="$2">
          {t('student.task.versionHistory.emptyHint', 'Versions are saved automatically as you write')}
        </Text>
      </Center>
    );
  }

  // Latest saved version (first in list) - this represents the last saved state

  return (
    <ScrollView flex={1} testID="version-history-list" bg="$white">
      <VStack p="$4" space="md">
        {/* Simple header text */}
        <Text size="xs" color="$textSubtle" mb="$1">
          {t('student.task.versionHistory.currentStatus', 'Your current draft has {{count}} characters', {
            count: currentCharCount,
          })}
        </Text>

        {/* Version list */}
        {versions.map((version, index) => {
          const charDiff = formatCharDiff(version.sizeChars, currentCharCount);
          const isLatest = index === 0;

          return (
            <Pressable
              key={version.versionSeq}
              testID={`version-item-${version.versionSeq}`}
              onPress={() => onLoadVersion(version.versionSeq)}
              bg="$white"
              borderRadius="$md"
              p="$3"
              borderWidth={1}
              borderColor="$borderLight200"
              sx={{
                ':hover': {
                  bg: '$backgroundLight50',
                  borderColor: '$borderLight300',
                },
              }}
            >
              <HStack justifyContent="space-between" alignItems="center">
                <VStack space="xs" flex={1}>
                  {/* Time row */}
                  <HStack space="sm" alignItems="center">
                    <Icon as={Clock} size="xs" color="$textHint" />
                    <Text size="sm" fontWeight="medium" color="$textBody">
                      {formatRelativeTime(version.createdAt)}
                    </Text>
                    {isLatest ? (
                      <Badge size="sm" variant="outline" action="muted" borderRadius="$sm">
                        <BadgeText size="2xs" color="$textSubtle">
                          {t('student.task.versionHistory.lastSaved', 'Last saved')}
                        </BadgeText>
                      </Badge>
                    ) : null}
                  </HStack>

                  {/* Stats row */}
                  <HStack space="md" alignItems="center">
                    <Text size="xs" color="$textSubtle">
                      {version.sizeChars.toLocaleString()}
                      {' '}
                      {t('student.task.versionHistory.characters', 'characters')}
                    </Text>
                    <HStack space="xs" alignItems="center">
                      <Icon as={charDiff.icon} size="xs" color={charDiff.color} />
                      <Text size="xs" fontWeight="medium" color={charDiff.color}>
                        {charDiff.text}
                      </Text>
                    </HStack>
                  </HStack>
                </VStack>

                {/* Action button */}
                <Button
                  size="xs"
                  variant="outline"
                  action="secondary"
                  onPress={(e) => {
                    e.stopPropagation();
                    onLoadVersion(version.versionSeq);
                  }}
                  testID={`load-version-${version.versionSeq}`}
                >
                  <ButtonIcon as={RotateCcw} size="xs" mr="$1" />
                  <ButtonText size="xs">
                    {t('student.task.versionHistory.restore', 'Restore')}
                  </ButtonText>
                </Button>
              </HStack>
            </Pressable>
          );
        })}
      </VStack>
    </ScrollView>
  );
};

export default VersionHistoryPanel;
